import {BaseComponent, UseEffect} from "react-vextensions";
import {Timer, WaitXThenRun} from "js-vextensions";
import {gql} from "@apollo/client";
import {useQuery, useSubscription} from "@apollo/client/react";
import {Button} from "react-vcomponents";
import {GetAppServerURL} from "../../../../Utils/LibIntegrations/Apollo.js";
import {OpenSignInPopup, SignInProvider} from "../UserPanel.js";
import {Observer} from "web-vcore";
import React, {useCallback, useMemo} from "react";

export const SignInButton: React.FC<{provider: SignInProvider, preferredUsername?: string, onJWTReceived: (jwt: string)=>void}> = ({provider, preferredUsername, onJWTReceived}) => {	
	const anchor = useMemo(()=>({
		stateCopy: {
			authLink: null as string|n,
			jwtResolved: null as boolean|n,
		},
	}), []);
	
	const [authLink, setAuthLink] = React.useState<string | null>(null);
	const [devSignInButtonClicked, setDevSignInButtonClicked] = React.useState(false);
	const [jwtResolved, setJwtResolved] = React.useState(false);
	Object.assign(anchor.stateCopy, {authLink, jwtResolved});

	const subActive = provider === "dev"
		? devSignInButtonClicked && !jwtResolved
		: !jwtResolved;

	// todo: may want to handle possible edge-case of auth-link "expiring", requiring retrieval of a new one (ie. starting a new subscription)
	const monthInSecs = 2629800; // 60 * 60 * 24 * 30;
	useSubscription(gql`
		subscription($input: SignInStartInput!) {
			signInStart(input: $input) { instructions authLink resultJWT }
		}
	`, {
		skip: !subActive,
		variables: {input: {provider, jwtDuration: monthInSecs, preferredUsername}},
		onData: info => {
			const gqlResult = (info.data.data as unknown as {signInStart: {instructions: unknown, authLink?: string, resultJWT?: string}}).signInStart;
			if (gqlResult.authLink) {
				setAuthLink(gqlResult.authLink);
			}

			if (gqlResult.resultJWT) {
				setJwtResolved(true);
				onJWTReceived(gqlResult.resultJWT);
			}
		},
	});

	// anchor this function to the class-instance, so that it doesn't change between re-renders (so google applied-styling isn't lost)
	const Google_OnClick = useCallback(()=>{
		const {authLink, jwtResolved} = anchor.stateCopy;
		// Why not block popup-relaunch if was already launched? Because user may have closed the popup, but then decided to try again.
		if (authLink != null && !jwtResolved) {
			OpenSignInPopup(authLink, provider);
		}
	}, []);

	return (
		<>
			{provider === "google" && <SignInButton_Google_Inner onClick={Google_OnClick} />}
			{provider === "dev" && <Button ml={5} text="Sign in" onClick={() => setDevSignInButtonClicked(true)} />}
		</>
	);
};

const SignInButton_Google_Inner: React.FC<{onClick: ()=>any}> = ({onClick}) => {
	const rootRef = React.useRef<HTMLDivElement | null>(null);

	UseEffect(() => {
		const timer = new Timer(100, () => {
			if (g.google != null && rootRef.current != null) {
				timer.Stop();

				// first clear out the div, so we don't disrupt the google-sign-in rendering
				rootRef.current.innerHTML = "";

				EnsureGoogleIDAPIReady();
				const options: GsiButtonConfiguration = {};
				g.google.accounts.id.renderButton(rootRef.current, options, onClick);
			}
		}).Start();
		return () => timer.Stop();
	}, [onClick]);

	return (
		// start button out as basic "Sign in with Google", but fill it in with the more fancy google-sign-in rendering, once completed
		// (if that fails though, eg. due to third-party cookies being disabled I think, button at least has its basic functionality)
		<div ref={rootRef}>
			<Button text="Sign in with Google" onClick={onClick} />
		</div>
	);
};

// from: https://developers.google.com/identity/gsi/web/reference/js-reference
type GsiButtonConfiguration = {
	type?: string; // The button type: icon, or standard button.
	theme?: string; // The button theme. For example, white or blue.
	size?: string; // The button size. For example, samll or large.
	text?: string; // The button text. For example, "Sign in with Google" or "Sign up with Google".
	shape?: string; // The button shape. For example, rectangular or circular.
	logo_alignment?: string; // The Google logo alignment: left or center.
	width?: number; // The button width, in pixels.
	locale?: string; // If set, then the button language is rendered.
}
export const googleClientID = process.env.CLIENT_ID; // supplied by client/rspack.config.js
export function EnsureGoogleIDAPIReady() {
	/*if (g.google == null) {
		console.error("Cannot initialize Google ID api, because its script has not been loaded.");
		return;
	}*/

	if (g.google.accounts.id._initCalled) return;
	/*const googleClientID_randomPart = googleClientID?.replace(".apps.googleusercontent.com", "");
	console.log("GClientID:", `${googleClientID_randomPart?.slice(0, 2)}...${googleClientID_randomPart?.slice(-2)}`);*/
	g.google.accounts.id.initialize({
		client_id: googleClientID,
		callback: googleID_handleCredentialResponse,
	});
	g.google.accounts.id._initCalled = true;
}
export type GoogleID_CredentialResponse = {clientId: string, credential: string, select_by: "string"};
export const googleID_handleCredentialResponse = async(response: GoogleID_CredentialResponse)=>{
	console.log("Data:", response);
	await fetch(GetAppServerURL("/auth/google/callback"), {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			idToken: response.credential,
		}),
	});
};